import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './MedicalProfile.css';

const MedicalProfile = () => {
  const { user } = useAuth();
  const toast = useToast();

  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Medical Profile State
  const [medicalProfile, setMedicalProfile] = useState(null);
  const [medications, setMedications] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [clearances, setClearances] = useState([]);

  // Modals
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [showInjuryModal, setShowInjuryModal] = useState(false);
  const [showClearanceModal, setShowClearanceModal] = useState(false);

  // Form Data
  const [profileFormData, setProfileFormData] = useState({
    blood_type: 'unknown',
    allergies: [],
    chronic_conditions: [],
    physical_restrictions: '',
    special_needs: '',
    diet_restrictions: '',
    primary_physician_name: '',
    primary_physician_phone: '',
    primary_physician_address: '',
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_group_number: '',
    additional_notes: '',
    last_physical_exam: '',
    next_physical_due: ''
  });

  const [medicationFormData, setMedicationFormData] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    purpose: '',
    prescribing_doctor: '',
    start_date: '',
    end_date: '',
    side_effects: '',
    special_instructions: '',
    requires_during_practice: false,
    stored_at_facility: false
  });

  const [injuryFormData, setInjuryFormData] = useState({
    injury_date: new Date().toISOString().split('T')[0],
    injury_type: '',
    body_part: '',
    severity: 'minor',
    description: '',
    how_it_occurred: '',
    treatment_plan: '',
    recovery_timeline: '',
    estimated_return_date: '',
    requires_clearance: false,
    affected_skills: [],
    restrictions: '',
    physical_therapy: false,
    surgery_required: false,
    coach_notes: '',
    parent_notified: false
  });

  const [clearanceFormData, setClearanceFormData] = useState({
    injury_id: '',
    clearance_type: 'full',
    cleared_by: '',
    provider_type: 'doctor',
    clearance_date: new Date().toISOString().split('T')[0],
    valid_until: '',
    restrictions: '',
    cleared_activities: [],
    prohibited_activities: [],
    requires_monitoring: false,
    monitoring_notes: '',
    documentation_url: ''
  });

  // Temp states for adding allergies/conditions
  const [newAllergy, setNewAllergy] = useState({ name: '', severity: 'mild', reaction: '' });
  const [newCondition, setNewCondition] = useState({ condition: '', diagnosed_date: '', notes: '' });

  useEffect(() => {
    fetchAthletes();
  }, []);

  useEffect(() => {
    if (selectedAthlete) {
      fetchMedicalData();
    }
  }, [selectedAthlete, activeTab]);

  const fetchAthletes = async () => {
    try {
      const token = user?.token;
      const response = await fetch(`${API_BASE_URL}/api/v1/athletes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAthletes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching athletes:', error);
      setAthletes([]);
    }
  };

  const fetchMedicalData = async () => {
    try {
      const token = user?.token;

      if (activeTab === 'profile' || activeTab === 'medications') {
        const response = await fetch(`${API_BASE_URL}/api/athletes/${selectedAthlete.id}/medical-profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setMedicalProfile(data.profile);
        setMedications(data.medications || []);

        if (data.profile) {
          setProfileFormData({
            ...data.profile,
            allergies: data.profile.allergies ? JSON.parse(data.profile.allergies) : [],
            chronic_conditions: data.profile.chronic_conditions ? JSON.parse(data.profile.chronic_conditions) : []
          });
        }
      }

      if (activeTab === 'injuries') {
        const response = await fetch(`${API_BASE_URL}/api/athletes/${selectedAthlete.id}/injuries`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setInjuries(Array.isArray(data) ? data : []);
      }

      if (activeTab === 'clearances') {
        const response = await fetch(`${API_BASE_URL}/api/athletes/${selectedAthlete.id}/clearances`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setClearances(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching medical data:', error);
    }
  };

  const handleSaveMedicalProfile = async (e) => {
    e.preventDefault();
    try {
      const token = user?.token;
      const response = await fetch(`${API_BASE_URL}/api/athletes/${selectedAthlete.id}/medical-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileFormData)
      });

      if (response.ok) {
        toast.success('Medical profile saved successfully!');
        setShowProfileModal(false);
        fetchMedicalData();
      } else {
        toast.error('Failed to save medical profile');
      }
    } catch (error) {
      console.error('Error saving medical profile:', error);
      toast.error('Error saving medical profile');
    }
  };

  const handleAddMedication = async (e) => {
    e.preventDefault();
    try {
      const token = user?.token;
      const response = await fetch(`${API_BASE_URL}/api/athletes/${selectedAthlete.id}/medications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(medicationFormData)
      });

      if (response.ok) {
        toast.success('Medication added successfully!');
        setShowMedicationModal(false);
        resetMedicationForm();
        fetchMedicalData();
      } else {
        toast.error('Failed to add medication');
      }
    } catch (error) {
      console.error('Error adding medication:', error);
      toast.error('Error adding medication');
    }
  };

  const handleReportInjury = async (e) => {
    e.preventDefault();
    try {
      const token = user?.token;
      const response = await fetch(`${API_BASE_URL}/api/athletes/${selectedAthlete.id}/injuries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(injuryFormData)
      });

      if (response.ok) {
        toast.success('Injury reported successfully!');
        setShowInjuryModal(false);
        resetInjuryForm();
        fetchMedicalData();
      } else {
        toast.error('Failed to report injury');
      }
    } catch (error) {
      console.error('Error reporting injury:', error);
      toast.error('Error reporting injury');
    }
  };

  const handleAddClearance = async (e) => {
    e.preventDefault();
    try {
      const token = user?.token;
      const response = await fetch(`${API_BASE_URL}/api/athletes/${selectedAthlete.id}/clearances`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clearanceFormData)
      });

      if (response.ok) {
        toast.success('Clearance added successfully!');
        setShowClearanceModal(false);
        resetClearanceForm();
        fetchMedicalData();
      } else {
        toast.error('Failed to add clearance');
      }
    } catch (error) {
      console.error('Error adding clearance:', error);
      toast.error('Error adding clearance');
    }
  };

  const addAllergy = () => {
    if (newAllergy.name) {
      setProfileFormData({
        ...profileFormData,
        allergies: [...profileFormData.allergies, { ...newAllergy }]
      });
      setNewAllergy({ name: '', severity: 'mild', reaction: '' });
    }
  };

  const removeAllergy = (index) => {
    const updated = profileFormData.allergies.filter((_, i) => i !== index);
    setProfileFormData({ ...profileFormData, allergies: updated });
  };

  const addCondition = () => {
    if (newCondition.condition) {
      setProfileFormData({
        ...profileFormData,
        chronic_conditions: [...profileFormData.chronic_conditions, { ...newCondition }]
      });
      setNewCondition({ condition: '', diagnosed_date: '', notes: '' });
    }
  };

  const removeCondition = (index) => {
    const updated = profileFormData.chronic_conditions.filter((_, i) => i !== index);
    setProfileFormData({ ...profileFormData, chronic_conditions: updated });
  };

  const resetMedicationForm = () => {
    setMedicationFormData({
      medication_name: '',
      dosage: '',
      frequency: '',
      purpose: '',
      prescribing_doctor: '',
      start_date: '',
      end_date: '',
      side_effects: '',
      special_instructions: '',
      requires_during_practice: false,
      stored_at_facility: false
    });
  };

  const resetInjuryForm = () => {
    setInjuryFormData({
      injury_date: new Date().toISOString().split('T')[0],
      injury_type: '',
      body_part: '',
      severity: 'minor',
      description: '',
      how_it_occurred: '',
      treatment_plan: '',
      recovery_timeline: '',
      estimated_return_date: '',
      requires_clearance: false,
      affected_skills: [],
      restrictions: '',
      physical_therapy: false,
      surgery_required: false,
      coach_notes: '',
      parent_notified: false
    });
  };

  const resetClearanceForm = () => {
    setClearanceFormData({
      injury_id: '',
      clearance_type: 'full',
      cleared_by: '',
      provider_type: 'doctor',
      clearance_date: new Date().toISOString().split('T')[0],
      valid_until: '',
      restrictions: '',
      cleared_activities: [],
      prohibited_activities: [],
      requires_monitoring: false,
      monitoring_notes: '',
      documentation_url: ''
    });
  };

  const getInjuryStatusBadge = (status) => {
    const badges = {
      active: 'badge-injury-active',
      recovering: 'badge-injury-recovering',
      cleared: 'badge-injury-cleared',
      chronic: 'badge-injury-chronic'
    };
    return badges[status] || 'badge-default';
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      minor: 'badge-severity-minor',
      moderate: 'badge-severity-moderate',
      severe: 'badge-severity-severe',
      critical: 'badge-severity-critical'
    };
    return badges[severity] || 'badge-default';
  };

  return (
    <div className="medical-profile-container">
      <div className="medical-header">
        <h1>🏥 Medical & Health Management</h1>
        <p>Holistic athlete health tracking and safety management</p>
      </div>

      {/* Athlete Selector */}
      <div className="athlete-selector">
        <label>Select Athlete:</label>
        <select 
          value={selectedAthlete?.id || ''} 
          onChange={(e) => {
            const athlete = athletes.find(a => a.id === parseInt(e.target.value));
            setSelectedAthlete(athlete);
          }}
        >
          <option value="">-- Choose Athlete --</option>
          {athletes.map(athlete => (
            <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
          ))}
        </select>
      </div>

      {selectedAthlete && (
        <>
          {/* Health Status Overview */}
          <div className="health-status-overview">
            <div className="status-card">
              <span className="status-icon">🩺</span>
              <div>
                <h3>Medical Profile</h3>
                <p>{medicalProfile ? 'Complete' : 'Incomplete'}</p>
              </div>
            </div>
            <div className="status-card">
              <span className="status-icon">💊</span>
              <div>
                <h3>Current Medications</h3>
                <p>{medications.length} active</p>
              </div>
            </div>
            <div className="status-card">
              <span className="status-icon">🤕</span>
              <div>
                <h3>Active Injuries</h3>
                <p>{injuries.filter(i => i.status === 'active' || i.status === 'recovering').length}</p>
              </div>
            </div>
            <div className="status-card">
              <span className="status-icon">✅</span>
              <div>
                <h3>Clearances</h3>
                <p>{clearances.length} on file</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="medical-tabs">
            <button 
              className={activeTab === 'profile' ? 'tab-active' : ''}
              onClick={() => setActiveTab('profile')}
            >
              Medical Profile
            </button>
            <button 
              className={activeTab === 'medications' ? 'tab-active' : ''}
              onClick={() => setActiveTab('medications')}
            >
              Medications
            </button>
            <button 
              className={activeTab === 'injuries' ? 'tab-active' : ''}
              onClick={() => setActiveTab('injuries')}
            >
              Injury History
            </button>
            <button 
              className={activeTab === 'clearances' ? 'tab-active' : ''}
              onClick={() => setActiveTab('clearances')}
            >
              Medical Clearances
            </button>
          </div>

          {/* Medical Profile Tab */}
          {activeTab === 'profile' && (
            <div className="tab-content">
              <div className="tab-header">
                <h2>Medical Profile</h2>
                <button className="btn-primary" onClick={() => setShowProfileModal(true)}>
                  {medicalProfile ? '✏️ Edit Profile' : '➕ Create Profile'}
                </button>
              </div>

              {medicalProfile ? (
                <div className="profile-display">
                  <div className="profile-section">
                    <h3>Basic Information</h3>
                    <div className="profile-grid">
                      <div><strong>Blood Type:</strong> {medicalProfile.blood_type}</div>
                      <div><strong>Last Physical:</strong> {medicalProfile.last_physical_exam || 'Not recorded'}</div>
                      <div><strong>Next Physical Due:</strong> {medicalProfile.next_physical_due || 'Not set'}</div>
                    </div>
                  </div>

                  <div className="profile-section">
                    <h3>Allergies</h3>
                    {profileFormData.allergies.length > 0 ? (
                      <ul className="allergy-list">
                        {profileFormData.allergies.map((allergy, idx) => (
                          <li key={idx} className={`allergy-item severity-${allergy.severity}`}>
                            <strong>{allergy.name}</strong> ({allergy.severity})
                            {allergy.reaction && <span> - {allergy.reaction}</span>}
                          </li>
                        ))}
                      </ul>
                    ) : <p>No allergies recorded</p>}
                  </div>

                  <div className="profile-section">
                    <h3>Chronic Conditions</h3>
                    {profileFormData.chronic_conditions.length > 0 ? (
                      <ul className="condition-list">
                        {profileFormData.chronic_conditions.map((condition, idx) => (
                          <li key={idx}>
                            <strong>{condition.condition}</strong>
                            {condition.diagnosed_date && <span> (Diagnosed: {condition.diagnosed_date})</span>}
                            {condition.notes && <p>{condition.notes}</p>}
                          </li>
                        ))}
                      </ul>
                    ) : <p>No chronic conditions recorded</p>}
                  </div>

                  <div className="profile-section">
                    <h3>Restrictions & Special Needs</h3>
                    <p><strong>Physical Restrictions:</strong> {medicalProfile.physical_restrictions || 'None'}</p>
                    <p><strong>Special Needs:</strong> {medicalProfile.special_needs || 'None'}</p>
                    <p><strong>Diet Restrictions:</strong> {medicalProfile.diet_restrictions || 'None'}</p>
                  </div>

                  <div className="profile-section">
                    <h3>Primary Physician</h3>
                    <p><strong>Name:</strong> {medicalProfile.primary_physician_name || 'Not provided'}</p>
                    <p><strong>Phone:</strong> {medicalProfile.primary_physician_phone || 'Not provided'}</p>
                    <p><strong>Address:</strong> {medicalProfile.primary_physician_address || 'Not provided'}</p>
                  </div>

                  <div className="profile-section">
                    <h3>Insurance Information</h3>
                    <p><strong>Provider:</strong> {medicalProfile.insurance_provider || 'Not provided'}</p>
                    <p><strong>Policy #:</strong> {medicalProfile.insurance_policy_number || 'Not provided'}</p>
                    <p><strong>Group #:</strong> {medicalProfile.insurance_group_number || 'Not provided'}</p>
                  </div>

                  {medicalProfile.additional_notes && (
                    <div className="profile-section">
                      <h3>Additional Notes</h3>
                      <p>{medicalProfile.additional_notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No medical profile on file. Click "Create Profile" to add medical information.</p>
                </div>
              )}
            </div>
          )}

          {/* Medications Tab */}
          {activeTab === 'medications' && (
            <div className="tab-content">
              <div className="tab-header">
                <h2>Current Medications</h2>
                <button className="btn-primary" onClick={() => setShowMedicationModal(true)}>
                  ➕ Add Medication
                </button>
              </div>

              {medications.length > 0 ? (
                <div className="medications-list">
                  {medications.map(med => (
                    <div key={med.id} className="medication-card">
                      <div className="medication-header">
                        <h3>{med.medication_name}</h3>
                        {med.requires_during_practice && <span className="badge-warning">⚠️ Required During Practice</span>}
                        {med.stored_at_facility && <span className="badge-info">🏢 Stored at Facility</span>}
                      </div>
                      <div className="medication-details">
                        <p><strong>Dosage:</strong> {med.dosage}</p>
                        <p><strong>Frequency:</strong> {med.frequency}</p>
                        <p><strong>Purpose:</strong> {med.purpose}</p>
                        {med.prescribing_doctor && <p><strong>Prescribed by:</strong> {med.prescribing_doctor}</p>}
                        {med.start_date && <p><strong>Start Date:</strong> {med.start_date}</p>}
                        {med.end_date && <p><strong>End Date:</strong> {med.end_date}</p>}
                        {med.side_effects && <p><strong>Side Effects:</strong> {med.side_effects}</p>}
                        {med.special_instructions && <p><strong>Instructions:</strong> {med.special_instructions}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No medications recorded. Click "Add Medication" to track medications.</p>
                </div>
              )}
            </div>
          )}

          {/* Injuries Tab */}
          {activeTab === 'injuries' && (
            <div className="tab-content">
              <div className="tab-header">
                <h2>Injury History</h2>
                <button className="btn-primary" onClick={() => setShowInjuryModal(true)}>
                  ➕ Report Injury
                </button>
              </div>

              {injuries.length > 0 ? (
                <div className="injuries-list">
                  {injuries.map(injury => (
                    <div key={injury.id} className="injury-card">
                      <div className="injury-header">
                        <div>
                          <h3>{injury.body_part} - {injury.injury_type}</h3>
                          <span className="injury-date">📅 {injury.injury_date}</span>
                        </div>
                        <div>
                          <span className={`badge ${getInjuryStatusBadge(injury.status)}`}>{injury.status}</span>
                          <span className={`badge ${getSeverityBadge(injury.severity)}`}>{injury.severity}</span>
                        </div>
                      </div>
                      <div className="injury-details">
                        <p><strong>Description:</strong> {injury.description}</p>
                        {injury.how_it_occurred && <p><strong>How it occurred:</strong> {injury.how_it_occurred}</p>}
                        {injury.treatment_plan && <p><strong>Treatment:</strong> {injury.treatment_plan}</p>}
                        {injury.recovery_timeline && <p><strong>Recovery Timeline:</strong> {injury.recovery_timeline}</p>}
                        {injury.estimated_return_date && <p><strong>Estimated Return:</strong> {injury.estimated_return_date}</p>}
                        {injury.actual_return_date && <p><strong>Actual Return:</strong> {injury.actual_return_date}</p>}
                        {injury.restrictions && <p><strong>Restrictions:</strong> {injury.restrictions}</p>}
                        {injury.requires_clearance && !injury.clearance_obtained && (
                          <p className="warning-text">⚠️ Medical clearance required before return</p>
                        )}
                        {injury.clearance_obtained && (
                          <p className="success-text">✅ Cleared by {injury.clearance_provider} on {injury.clearance_date}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No injuries recorded. This is great news! 🎉</p>
                </div>
              )}
            </div>
          )}

          {/* Clearances Tab */}
          {activeTab === 'clearances' && (
            <div className="tab-content">
              <div className="tab-header">
                <h2>Medical Clearances</h2>
                <button className="btn-primary" onClick={() => setShowClearanceModal(true)}>
                  ➕ Add Clearance
                </button>
              </div>

              {clearances.length > 0 ? (
                <div className="clearances-list">
                  {clearances.map(clearance => (
                    <div key={clearance.id} className="clearance-card">
                      <div className="clearance-header">
                        <h3>{clearance.clearance_type.toUpperCase()} Clearance</h3>
                        <span className="clearance-date">📅 {clearance.clearance_date}</span>
                      </div>
                      <div className="clearance-details">
                        <p><strong>Cleared by:</strong> {clearance.cleared_by} ({clearance.provider_type})</p>
                        {clearance.injury_description && <p><strong>Related to:</strong> {clearance.body_part} - {clearance.injury_description}</p>}
                        {clearance.valid_until && <p><strong>Valid Until:</strong> {clearance.valid_until}</p>}
                        {clearance.restrictions && <p><strong>Restrictions:</strong> {clearance.restrictions}</p>}
                        {clearance.requires_monitoring && (
                          <div className="monitoring-notice">
                            <p>⚠️ Requires Monitoring</p>
                            {clearance.monitoring_notes && <p>{clearance.monitoring_notes}</p>}
                          </div>
                        )}
                        <div className="clearance-acknowledgments">
                          {clearance.coach_acknowledged ? '✅ Coach Acknowledged' : '⏳ Pending Coach'}
                          {clearance.parent_acknowledged && ' | ✅ Parent Acknowledged'}
                          {clearance.athlete_acknowledged && ' | ✅ Athlete Acknowledged'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No medical clearances on file.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Medical Profile Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Medical Profile - {selectedAthlete.name}</h2>
            <form onSubmit={handleSaveMedicalProfile}>
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Blood Type</label>
                    <select
                      value={profileFormData.blood_type}
                      onChange={(e) => setProfileFormData({...profileFormData, blood_type: e.target.value})}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Last Physical Exam</label>
                    <input
                      type="date"
                      value={profileFormData.last_physical_exam}
                      onChange={(e) => setProfileFormData({...profileFormData, last_physical_exam: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Next Physical Due</label>
                    <input
                      type="date"
                      value={profileFormData.next_physical_due}
                      onChange={(e) => setProfileFormData({...profileFormData, next_physical_due: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Allergies</h3>
                <div className="allergies-manager">
                  {profileFormData.allergies.map((allergy, idx) => (
                    <div key={idx} className="allergy-chip">
                      {allergy.name} ({allergy.severity})
                      <button type="button" onClick={() => removeAllergy(idx)}>×</button>
                    </div>
                  ))}
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Allergen name"
                    value={newAllergy.name}
                    onChange={(e) => setNewAllergy({...newAllergy, name: e.target.value})}
                  />
                  <select
                    value={newAllergy.severity}
                    onChange={(e) => setNewAllergy({...newAllergy, severity: e.target.value})}
                  >
                    <option value="mild">Mild</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                    <option value="anaphylaxis">Anaphylaxis</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Reaction"
                    value={newAllergy.reaction}
                    onChange={(e) => setNewAllergy({...newAllergy, reaction: e.target.value})}
                  />
                  <button type="button" onClick={addAllergy} className="btn-secondary">Add</button>
                </div>
              </div>

              <div className="form-section">
                <h3>Chronic Conditions</h3>
                <div className="conditions-manager">
                  {profileFormData.chronic_conditions.map((condition, idx) => (
                    <div key={idx} className="condition-chip">
                      {condition.condition}
                      <button type="button" onClick={() => removeCondition(idx)}>×</button>
                    </div>
                  ))}
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Condition name"
                    value={newCondition.condition}
                    onChange={(e) => setNewCondition({...newCondition, condition: e.target.value})}
                  />
                  <input
                    type="date"
                    placeholder="Diagnosed date"
                    value={newCondition.diagnosed_date}
                    onChange={(e) => setNewCondition({...newCondition, diagnosed_date: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Notes"
                    value={newCondition.notes}
                    onChange={(e) => setNewCondition({...newCondition, notes: e.target.value})}
                  />
                  <button type="button" onClick={addCondition} className="btn-secondary">Add</button>
                </div>
              </div>

              <div className="form-section">
                <h3>Restrictions & Special Needs</h3>
                <div className="form-group">
                  <label>Physical Restrictions</label>
                  <textarea
                    value={profileFormData.physical_restrictions}
                    onChange={(e) => setProfileFormData({...profileFormData, physical_restrictions: e.target.value})}
                    placeholder="Any physical limitations or restrictions..."
                  />
                </div>
                <div className="form-group">
                  <label>Special Needs</label>
                  <textarea
                    value={profileFormData.special_needs}
                    onChange={(e) => setProfileFormData({...profileFormData, special_needs: e.target.value})}
                    placeholder="Any special accommodations needed..."
                  />
                </div>
                <div className="form-group">
                  <label>Diet Restrictions</label>
                  <textarea
                    value={profileFormData.diet_restrictions}
                    onChange={(e) => setProfileFormData({...profileFormData, diet_restrictions: e.target.value})}
                    placeholder="Dietary restrictions or preferences..."
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Primary Physician</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Physician Name</label>
                    <input
                      type="text"
                      value={profileFormData.primary_physician_name}
                      onChange={(e) => setProfileFormData({...profileFormData, primary_physician_name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={profileFormData.primary_physician_phone}
                      onChange={(e) => setProfileFormData({...profileFormData, primary_physician_phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    value={profileFormData.primary_physician_address}
                    onChange={(e) => setProfileFormData({...profileFormData, primary_physician_address: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Insurance Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Provider</label>
                    <input
                      type="text"
                      value={profileFormData.insurance_provider}
                      onChange={(e) => setProfileFormData({...profileFormData, insurance_provider: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Policy Number</label>
                    <input
                      type="text"
                      value={profileFormData.insurance_policy_number}
                      onChange={(e) => setProfileFormData({...profileFormData, insurance_policy_number: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Group Number</label>
                    <input
                      type="text"
                      value={profileFormData.insurance_group_number}
                      onChange={(e) => setProfileFormData({...profileFormData, insurance_group_number: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  value={profileFormData.additional_notes}
                  onChange={(e) => setProfileFormData({...profileFormData, additional_notes: e.target.value})}
                  placeholder="Any other medical information..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowProfileModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Profile
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Medication Modal */}
      {showMedicationModal && (
        <div className="modal-overlay" onClick={() => setShowMedicationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Medication</h2>
            <form onSubmit={handleAddMedication}>
              <div className="form-row">
                <div className="form-group">
                  <label>Medication Name *</label>
                  <input
                    type="text"
                    required
                    value={medicationFormData.medication_name}
                    onChange={(e) => setMedicationFormData({...medicationFormData, medication_name: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Dosage</label>
                  <input
                    type="text"
                    placeholder="e.g., 10mg"
                    value={medicationFormData.dosage}
                    onChange={(e) => setMedicationFormData({...medicationFormData, dosage: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Frequency</label>
                  <input
                    type="text"
                    placeholder="e.g., twice daily"
                    value={medicationFormData.frequency}
                    onChange={(e) => setMedicationFormData({...medicationFormData, frequency: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Purpose</label>
                <textarea
                  value={medicationFormData.purpose}
                  onChange={(e) => setMedicationFormData({...medicationFormData, purpose: e.target.value})}
                  placeholder="What is this medication for?"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Prescribing Doctor</label>
                  <input
                    type="text"
                    value={medicationFormData.prescribing_doctor}
                    onChange={(e) => setMedicationFormData({...medicationFormData, prescribing_doctor: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={medicationFormData.start_date}
                    onChange={(e) => setMedicationFormData({...medicationFormData, start_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={medicationFormData.end_date}
                    onChange={(e) => setMedicationFormData({...medicationFormData, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Side Effects</label>
                <textarea
                  value={medicationFormData.side_effects}
                  onChange={(e) => setMedicationFormData({...medicationFormData, side_effects: e.target.value})}
                  placeholder="Any known side effects..."
                />
              </div>

              <div className="form-group">
                <label>Special Instructions</label>
                <textarea
                  value={medicationFormData.special_instructions}
                  onChange={(e) => setMedicationFormData({...medicationFormData, special_instructions: e.target.value})}
                  placeholder="Special administration instructions..."
                />
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={medicationFormData.requires_during_practice}
                    onChange={(e) => setMedicationFormData({...medicationFormData, requires_during_practice: e.target.checked})}
                  />
                  Requires during practice
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={medicationFormData.stored_at_facility}
                    onChange={(e) => setMedicationFormData({...medicationFormData, stored_at_facility: e.target.checked})}
                  />
                  Stored at facility
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => {setShowMedicationModal(false); resetMedicationForm();}} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Medication
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Report Injury Modal */}
      {showInjuryModal && (
        <div className="modal-overlay" onClick={() => setShowInjuryModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Report Injury - {selectedAthlete.name}</h2>
            <form onSubmit={handleReportInjury}>
              <div className="form-row">
                <div className="form-group">
                  <label>Injury Date *</label>
                  <input
                    type="date"
                    required
                    value={injuryFormData.injury_date}
                    onChange={(e) => setInjuryFormData({...injuryFormData, injury_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Body Part *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Ankle, Wrist, Back"
                    value={injuryFormData.body_part}
                    onChange={(e) => setInjuryFormData({...injuryFormData, body_part: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Severity *</label>
                  <select
                    required
                    value={injuryFormData.severity}
                    onChange={(e) => setInjuryFormData({...injuryFormData, severity: e.target.value})}
                  >
                    <option value="minor">Minor</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Injury Type</label>
                <input
                  type="text"
                  placeholder="e.g., Sprain, Strain, Fracture"
                  value={injuryFormData.injury_type}
                  onChange={(e) => setInjuryFormData({...injuryFormData, injury_type: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  required
                  value={injuryFormData.description}
                  onChange={(e) => setInjuryFormData({...injuryFormData, description: e.target.value})}
                  placeholder="Describe the injury..."
                />
              </div>

              <div className="form-group">
                <label>How It Occurred</label>
                <textarea
                  value={injuryFormData.how_it_occurred}
                  onChange={(e) => setInjuryFormData({...injuryFormData, how_it_occurred: e.target.value})}
                  placeholder="What skill/activity was being performed?"
                />
              </div>

              <div className="form-group">
                <label>Treatment Plan</label>
                <textarea
                  value={injuryFormData.treatment_plan}
                  onChange={(e) => setInjuryFormData({...injuryFormData, treatment_plan: e.target.value})}
                  placeholder="Recommended treatment..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Recovery Timeline</label>
                  <input
                    type="text"
                    placeholder="e.g., 2-4 weeks"
                    value={injuryFormData.recovery_timeline}
                    onChange={(e) => setInjuryFormData({...injuryFormData, recovery_timeline: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Estimated Return Date</label>
                  <input
                    type="date"
                    value={injuryFormData.estimated_return_date}
                    onChange={(e) => setInjuryFormData({...injuryFormData, estimated_return_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Restrictions</label>
                <textarea
                  value={injuryFormData.restrictions}
                  onChange={(e) => setInjuryFormData({...injuryFormData, restrictions: e.target.value})}
                  placeholder="Training restrictions or limitations..."
                />
              </div>

              <div className="form-group">
                <label>Coach Notes</label>
                <textarea
                  value={injuryFormData.coach_notes}
                  onChange={(e) => setInjuryFormData({...injuryFormData, coach_notes: e.target.value})}
                  placeholder="Additional notes..."
                />
              </div>

              <div className="form-row checkbox-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={injuryFormData.requires_clearance}
                    onChange={(e) => setInjuryFormData({...injuryFormData, requires_clearance: e.target.checked})}
                  />
                  Requires medical clearance
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={injuryFormData.physical_therapy}
                    onChange={(e) => setInjuryFormData({...injuryFormData, physical_therapy: e.target.checked})}
                  />
                  Physical therapy needed
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={injuryFormData.surgery_required}
                    onChange={(e) => setInjuryFormData({...injuryFormData, surgery_required: e.target.checked})}
                  />
                  Surgery required
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={injuryFormData.parent_notified}
                    onChange={(e) => setInjuryFormData({...injuryFormData, parent_notified: e.target.checked})}
                  />
                  Parent notified
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => {setShowInjuryModal(false); resetInjuryForm();}} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Report Injury
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Clearance Modal */}
      {showClearanceModal && (
        <div className="modal-overlay" onClick={() => setShowClearanceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Medical Clearance</h2>
            <form onSubmit={handleAddClearance}>
              <div className="form-row">
                <div className="form-group">
                  <label>Clearance Type *</label>
                  <select
                    required
                    value={clearanceFormData.clearance_type}
                    onChange={(e) => setClearanceFormData({...clearanceFormData, clearance_type: e.target.value})}
                  >
                    <option value="full">Full Clearance</option>
                    <option value="modified">Modified Clearance</option>
                    <option value="restricted">Restricted Clearance</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Cleared By *</label>
                  <input
                    type="text"
                    required
                    placeholder="Doctor name"
                    value={clearanceFormData.cleared_by}
                    onChange={(e) => setClearanceFormData({...clearanceFormData, cleared_by: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Provider Type *</label>
                  <select
                    required
                    value={clearanceFormData.provider_type}
                    onChange={(e) => setClearanceFormData({...clearanceFormData, provider_type: e.target.value})}
                  >
                    <option value="doctor">Doctor</option>
                    <option value="physical_therapist">Physical Therapist</option>
                    <option value="athletic_trainer">Athletic Trainer</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Clearance Date *</label>
                  <input
                    type="date"
                    required
                    value={clearanceFormData.clearance_date}
                    onChange={(e) => setClearanceFormData({...clearanceFormData, clearance_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Valid Until</label>
                  <input
                    type="date"
                    value={clearanceFormData.valid_until}
                    onChange={(e) => setClearanceFormData({...clearanceFormData, valid_until: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Restrictions</label>
                <textarea
                  value={clearanceFormData.restrictions}
                  onChange={(e) => setClearanceFormData({...clearanceFormData, restrictions: e.target.value})}
                  placeholder="Any restrictions or limitations..."
                />
              </div>

              <div className="form-group">
                <label>Monitoring Notes</label>
                <textarea
                  value={clearanceFormData.monitoring_notes}
                  onChange={(e) => setClearanceFormData({...clearanceFormData, monitoring_notes: e.target.value})}
                  placeholder="Notes for ongoing monitoring..."
                />
              </div>

              <div className="form-group">
                <label>Documentation URL</label>
                <input
                  type="url"
                  value={clearanceFormData.documentation_url}
                  onChange={(e) => setClearanceFormData({...clearanceFormData, documentation_url: e.target.value})}
                  placeholder="Link to clearance letter/document"
                />
              </div>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={clearanceFormData.requires_monitoring}
                  onChange={(e) => setClearanceFormData({...clearanceFormData, requires_monitoring: e.target.checked})}
                />
                Requires ongoing monitoring
              </label>

              <div className="modal-actions">
                <button type="button" onClick={() => {setShowClearanceModal(false); resetClearanceForm();}} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Clearance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalProfile;
